<template>
  <div>
    <form>
      <div class="form-group">
        <!-- Name -->
        <div class="row mb-2">
          <div class="input-group">
            <div class="input-group-prepend">
              <div class="input-group-text">Nazwa kategorii</div>
            </div>
            <input
                v-model="name"
                type="text"
                :class="nameInputClass"
                id="category-name-input"
                placeholder="Wpisz nazwę kategorii"
                aria-describedby="category-name-input-feedback"
            />
            <div class="invalid-feedback" id="category-name-input-feedback">
              Nazwa kategorii jest wymagana.
            </div>
          </div>
        </div>

        <!-- Description -->
        <div class="row mb-2">
          <div class="input-group">
            <div class="input-group-prepend">
              <div class="input-group-text">Opis</div>
            </div>
            <textarea
                v-model="description"
                class="form-control"
                id="category-description-input"
                placeholder="Wpisz opis kategorii"
            ></textarea>
          </div>
        </div>

        <!-- Type -->
        <div class="row mb-2">
          <div class="input-group">
            <div class="input-group-prepend">
              <div class="input-group-text">Typ kategorii</div>
            </div>
            <select
                v-model="typeFriendly"
                class="form-select form-control"
                id="category-type-select"
            >
              <option v-for="opt in typeOptionsFriendly" :key="opt" :value="opt">{{ opt }}</option>
            </select>
          </div>
        </div>

        <!-- Order index -->
        <div class="row mb-2">
          <div class="input-group">
            <div class="input-group-prepend">
              <div class="input-group-text">Kolejność wyświetlania</div>
            </div>
            <input
                v-model="orderIndexInput"
                type="number"
                min="0"
                max="999"
                :class="orderIndexInputClass"
                id="category-order-index-input"
                aria-describedby="category-order-index-input-feedback"
            />
            <div class="invalid-feedback" id="category-order-index-input-feedback">
              Wartość musi być liczbą z zakresu 0-999.
            </div>
          </div>
        </div>

        <!-- Central category -->
        <div class="row mb-2">
          <div class="col">
            <div class="form-check">
              <input class="form-check-input" type="checkbox" v-model="isCentralCategory"
                     id="category-central-checkbox">
              <label class="form-check-label" for="category-central-checkbox">Kategoria centralna</label>
            </div>
          </div>
        </div>

        <!-- Parent category picker -->
        <div class="row mb-3">
          <div class="input-group">
            <div class="input-group-prepend">
              <div class="input-group-text">Kategoria nadrzędna</div>
            </div>
            <input
                :value="assignedParentCategoryValue"
                type="text"
                :class="parentCategoryInputClass"
                id="category-parent-input"
                placeholder="Wybierz kategorię nadrzędną"
                aria-describedby="category-parent-input-feedback"
                readonly
            />
            <div class="input-group-prepend" @click="openParentAssigningForm">
              <div class="btn btn-success">Zmień przypisanie</div>
            </div>
            <div class="input-group-prepend" v-if="parentCategoryObject" @click="clearParentCategory">
              <div class="btn btn-outline-secondary">Wyczyść</div>
            </div>
            <div class="invalid-feedback" id="category-parent-input-feedback">
              Wybierz prawidłową kategorię nadrzędną.
            </div>
          </div>
        </div>

        <!-- Footer actions -->
        <div class="d-flex justify-content-end gap-2">
          <button type="button" class="btn btn-primary" @click="saveData">Zapisz</button>
        </div>
      </div>
    </form>

    <ParentProductCategoriesListModal
        :showModal="showParentPicker"
        @update:showModal="showParentPicker = $event"
        :multiselect="false"
        :showAddButton="false"
        :showRowActions="true"
        :showMassActions="false"
        :selectedCategoriesData="parentCategoryObject ? [parentCategoryObject] : []"
        :categoryGuidForWhichWeSelectParent="categoryGuidForWhichWeSelectParent ? categoryGuidForWhichWeSelectParent.guid : null"
        okTitle="Wybierz"
        @onApply="onApplyParentSelection"
        @onClose="showParentPicker = false"
    />

  </div>
</template>

<script>
import {defineComponent} from 'vue'
import ParentProductCategoriesListModal from "@/views/DrugstoreCommonManager/ParentProductCategoriesListModal.vue";
import {useSimplepharmAuthModule} from "@/state/pinia/simplepharmAuthModule";
import {mapStores} from "pinia";
import {useCommonAppModule} from "@/state/pinia/commonAppModule";
import {useLazyLoaderModule} from "@/state/pinia/lazyloaderManagerModule";


export default defineComponent({
  name: 'CategoryForm',
  props: {
    initialCategoryData: {
      type: Object,
      required: false
    },
    categoryGuidForWhichWeSelectParent: {type: String, required: false},
  },
  emits: ['onSave'],
  components: {ParentProductCategoriesListModal},
  data() {
    return {
      guid: null,
      name: '',
      description: '',
      typeFriendly: 'GRUPA ASORTYMENTOWA',
      orderIndexInput: '0',
      isCentralCategory: false,
      parentCategoryObject: null,

      nameInputClass: 'form-control',
      orderIndexInputClass: 'form-control',
      parentCategoryInputClass: 'form-control',

      showParentPicker: false,
      searchQuery: '',
      parentCandidates: [],
      loading: false,

      typeOptionsFriendly: ['GRUPA ASORTYMENTOWA', 'ZAMIENNIK WŁASNY', 'INNY'],
      typeMapToCode: {
        'GRUPA ASORTYMENTOWA': 'ASSORTMENT_GROUP',
        'ZAMIENNIK WŁASNY': 'OWN_REPLACEMENT',
        'INNY': 'OTHER'
      },
      typeMapToFriendly: {
        'ASSORTMENT_GROUP': 'GRUPA ASORTYMENTOWA',
        'OWN_REPLACEMENT': 'ZAMIENNIK WŁASNY',
        'OTHER': 'INNY'
      }
    }
  },
  computed: {
    simplepharmAuth() {
      return useSimplepharmAuthModule();
    },
    commonModule() {
      return useCommonAppModule();
    },
    lazyloaderModule() {
      return useLazyLoaderModule();
    },
    preselectedParentArray() {
      if (this.parentCategoryObject) {
        return [this.parentCategoryObject]
      }
      if (this.initialCategoryData && this.initialCategoryData.parentCategoryGuid) {
        return [{guid: this.initialCategoryData.parentCategoryGuid}]
      }
      return []
    },
    assignedParentCategoryValue() {
      if (this.parentCategoryObject && Object.keys(this.parentCategoryObject).length > 0) {
        return `${this.parentCategoryObject.id}-${this.parentCategoryObject.name}`
      }
      return 'Nie wybrano kategorii'
    }
  },
  methods: {

    onApplyParentSelection(items) {
      if (items && items.length > 0) {
        const picked = items[0]
        // If we only got a guid (from preselected), refetch
        if (picked && (!picked.name || !picked.guid)) {
          // fallback: do nothing special; selection should provide full item data
        }
        this.parentCategoryObject = picked
      }
      this.showParentPicker = false
    },
    openParentAssigningForm() {
      this.showParentPicker = true
    },
    clearParentCategory() {
      this.parentCategoryObject = null
    },
    saveData() {
      // Validate name
      let invalidCount = 0
      if (!this.name || this.name.trim().length < 2) {
        this.nameInputClass = 'form-control is-invalid'
        invalidCount++
      } else {
        this.nameInputClass = 'form-control'
      }

      // Validate orderIndex -> cast number
      let parsedOrder = Number(this.orderIndexInput)
      if (!Number.isFinite(parsedOrder) || parsedOrder < 0) {
        parsedOrder = 0
      }
      // show invalid class only if non-numeric and not empty negative etc.
      if (parsedOrder < 0 || parsedOrder > 999) {
        this.orderIndexInputClass = 'form-control is-invalid'
        invalidCount++
      } else {
        this.orderIndexInputClass = 'form-control'
      }

      if (invalidCount > 0) {
        return
      }

      const typeCode = this.typeMapToCode[this.typeFriendly] || this.typeFriendly

      const payload = {
        guid: this.guid || null,
        name: this.name ? this.name.trim() : '',
        description: this.description || '',
        type: typeCode,
        orderIndex: parsedOrder,
        isCentralCategory: !!this.isCentralCategory,
        parentCategoryGuid: this.parentCategoryObject ? this.parentCategoryObject.guid : (this.initialCategoryData && this.initialCategoryData.parentCategoryGuid ? this.initialCategoryData.parentCategoryGuid : null),
        parentCategoryId: this.parentCategoryObject ? this.parentCategoryObject.id : (this.initialCategoryData && this.initialCategoryData.parentCategoryId ? this.initialCategoryData.parentCategoryId : null),
      }

      this.$emit('onSave', payload)
    }
  },
  watch: {
    orderIndexInput(newVal) {
      // Remove non-numeric characters
      let parsedOrder = Number(newVal)
      if (!Number.isFinite(parsedOrder) || parsedOrder < 0) {
        this.orderIndexInput = '0'
      } else {
        if (parsedOrder > 999) {
          parsedOrder = 999
        }
        this.orderIndexInput = String(Math.floor(parsedOrder))
      }

    }
  },
  created() {
    if (this.initialCategoryData) {
      const d = this.initialCategoryData
      this.guid = d.guid || null
      this.name = d.name || ''
      this.description = d.description || ''
      // determine typeFriendly from code or leave as provided
      this.typeFriendly = this.typeMapToFriendly[d.type] || (d.type && this.typeOptionsFriendly.includes(d.type) ? d.type : 'GRUPA ASORTYMENTOWA')
      this.orderIndexInput = (d.orderIndex !== undefined && d.orderIndex !== null) ? String(d.orderIndex) : '0'
      this.isCentralCategory = !!d.isCentralCategory

      const parentGuid = d.parentCategoryGuid || null
      if (parentGuid) {
        this.commonModule.httpGetCategoryByGuid({guid: parentGuid}).then((response) => {
          this.parentCategoryObject = response.data.payload
        })
      }
    }
  }
})
</script>
<style scoped>
</style>
